/**
 * DeliverableView — format-aware renderer for /deliverable.md.
 *
 * Reads the deliverable type from a top-level HTML comment
 * (<!-- deliverable_type: KEY -->) or the store-backed `deliverableType`
 * override, and applies per-format chrome: a colored format badge,
 * a live table-of-contents for long-form formats, a step counter for
 * how-to guides, and export affordances.
 *
 * Stays on the same virtual-FS pipeline as VirtualFileViewer — the file
 * content still arrives via deep_state_snapshot. This component only
 * adds the presentation layer.
 */
import type { ReactNode } from "react"
import { useCallback, useEffect, useMemo, useRef } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { DeepFile } from "@/stores/ai-explorer-store"

import { FileSpreadsheet, Image as ImageIcon, Printer } from "@/components/icons"
import { MarkdownRenderer } from "@/components/markdown-renderer"
import { parseAnswerToHtml } from "@/features/chat/components/answer-parser"
import { CitationInlinePeek } from "@/features/chat/components/citation-inline-peek"
import { createCitationLinkComponent } from "@/features/chat/components/citation-link"
import { cn } from "@/lib/utils"

import { hasCanvasBlocks, parseCanvasBlocks } from "./canvas-blocks"
import { CanvasBlockRenderer } from "./CanvasBlockRenderer"
import {
  DEFAULT_FORMAT,
  type DeliverableType,
  FORMAT_INFO,
  parseDeliverableType,
} from "./deliverable-format-utils"
import { downloadSvg, printDeliverableAsPdf } from "../../utils/exportDeliverable"
import { downloadCsv, markdownTableToCsv } from "../../utils/markdownTableToCsv"

interface TocEntry {
  level: number // 2 = ##, 3 = ###
  text: string
  anchor: string
}

function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
}

function buildToc(content: string): TocEntry[] {
  const entries: TocEntry[] = []
  const lines = content.split("\n")
  let inFence = false
  for (const line of lines) {
    if (line.startsWith("```")) {
      inFence = !inFence
      continue
    }
    if (inFence) continue
    const match = /^(#{2,3})\s+(.+?)\s*$/.exec(line)
    if (!match) continue
    const level = match[1].length
    const text = match[2].replace(/[#`*_]/g, "").trim()
    if (!text) continue
    entries.push({ level, text, anchor: slugifyHeading(text) })
  }
  return entries
}

function countSteps(content: string): number {
  // Count "### <N>." step headers typical for howto_guide skeleton.
  const re = /^###\s+\d+\.\s/gm
  const matches = content.match(re)
  return matches?.length ?? 0
}

function countCitations(content: string): number {
  // Matches [[...]] excluding fenced code blocks. Cheap approximation.
  const re = /\[\[([^\]\n]+)\]\]/g
  const matches = content.match(re)
  return matches?.length ?? 0
}

interface DeliverableViewProps {
  file: DeepFile
  /** Optional override of the type (e.g. from a store field). Falls back to
   *  parsing the content, then to DEFAULT_FORMAT. */
  deliverableType?: DeliverableType | null
  /** When true, the file is still streaming in — show a subtle pulse. */
  isStreaming?: boolean
  /** Invoked when the user clicks the "{N} sources" badge and no References
   *  section exists in the rendered report — the parent switches the side
   *  panel to Activity→Sources so the user actually sees the sources. */
  onSourcesRequested?: () => void
  /** Invoked with the raw citation string when the user clicks an inline
   *  citation badge in the rendered report. Mirrors AI Explorer's
   *  system-message handler — parent routes to the sources panel /
   *  preview. */
  onCitationClick?: (citation: string) => void
  /** Search-service / index id passed to the in-deliverable hover-peek so
   *  `getCitationFilePath` can build the right `/api/content/<section>/…`
   *  URL. When omitted the peek is suppressed (no preview without a path). */
  citationSection?: string
  /** Invoked when the user clicks "Open" inside the hover-peek card. The
   *  parent should resolve the citation (e.g. repair bare filenames against
   *  the source list) and open the inline viewer. */
  onCitationViewDocument?: (
    citation: string,
    citationSection: string,
    options?: { chunkText?: string }
  ) => void
  /** Extra file-level actions supplied by the shell, e.g. save/export markdown. */
  headerActions?: ReactNode
}

export function DeliverableView({
  file,
  deliverableType,
  isStreaming = false,
  onSourcesRequested,
  onCitationClick,
  citationSection,
  onCitationViewDocument,
  headerActions,
}: DeliverableViewProps) {
  const { t } = useTranslation()
  const resolvedType: DeliverableType =
    deliverableType ?? parseDeliverableType(file.content) ?? DEFAULT_FORMAT
  const info = FORMAT_INFO[resolvedType]
  const formatLabel = t(info.labelKey)
  const contentRef = useRef<HTMLDivElement>(null)

  const toc = useMemo(
    () => (info.showToc ? buildToc(file.content) : []),
    [file.content, info.showToc]
  )
  const stepCount = useMemo(
    () => (info.showStepCounter ? countSteps(file.content) : 0),
    [file.content, info.showStepCounter]
  )
  const citationCount = useMemo(() => countCitations(file.content), [file.content])

  const { answerHtml, citations } = useMemo(
    () =>
      parseAnswerToHtml({
        answer: file.content,
        isStreaming,
        noCitation: false,
        citationSection: "",
      }),
    [file.content, isStreaming]
  )
  const citationComponents = useMemo(
    () => ({
      a: createCitationLinkComponent(
        () => citations,
        () => ""
      ),
    }),
    [citations]
  )
  const hasMermaid = useMemo(() => /```\s*mermaid\s/i.test(file.content), [file.content])
  const csvPreview = useMemo(() => markdownTableToCsv(file.content), [file.content])
  const canExportCsv = csvPreview !== null
  // UI-1: detect typed canvas blocks. If any are present, switch the body
  // to the typed-block renderer; otherwise stay on the markdown pipeline so
  // current deliverables (no blocks) render exactly as before.
  const useCanvasBlocks = useMemo(() => hasCanvasBlocks(file.content), [file.content])
  const canvasBlocks = useMemo(
    () => (useCanvasBlocks ? parseCanvasBlocks(file.content) : []),
    [useCanvasBlocks, file.content]
  )

  // MarkdownRenderer doesn't run rehype-slug, so headings render without `id`
  // attributes. Inject them ourselves using the same slug algorithm as
  // buildToc, so ToC click-handlers can find their targets via querySelector.
  useEffect(() => {
    const root = contentRef.current
    if (!root) return
    root.querySelectorAll<HTMLHeadingElement>("h2, h3").forEach((heading) => {
      const text = heading.textContent.trim()
      if (!text) return
      const slug = slugifyHeading(text)
      if (slug && heading.id !== slug) {
        heading.id = slug
      }
    })
  }, [answerHtml])

  const scrollToAnchor = useCallback((anchor: string) => {
    const root = contentRef.current
    if (!root) return
    const target = root.querySelector<HTMLElement>(`[id="${CSS.escape(anchor)}"]`)
    if (!target) return
    target.scrollIntoView({ behavior: "smooth", block: "start" })
    // Update the URL hash without triggering native anchor-scroll (which
    // would race our programmatic scroll and try to move the viewport).
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#${anchor}`)
    }
  }, [])

  const handleContentClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!onCitationClick) return
      const target = e.target as HTMLElement
      const supContainer = target.closest(".supContainer")
      if (!supContainer) return
      const citation = supContainer.getAttribute("data-citation-raw")
      if (citation) {
        onCitationClick(citation)
      }
    },
    [onCitationClick]
  )

  const REFERENCES_HEADING_RE = /^(references|källor|sources|källförteckning|bibliography)$/i

  const handleSourcesBadgeClick = useCallback(() => {
    const root = contentRef.current
    if (root) {
      const headings = root.querySelectorAll<HTMLHeadingElement>("h2, h3")
      for (const h of headings) {
        const text = h.textContent.trim()
        if (REFERENCES_HEADING_RE.test(text)) {
          h.scrollIntoView({ behavior: "smooth", block: "start" })
          return
        }
      }
    }
    onSourcesRequested?.()
  }, [onSourcesRequested])

  const deriveBaseFilename = () => {
    const firstH1 = /^#\s+(.+)$/m.exec(file.content)
    const raw = firstH1 ? firstH1[1] : formatLabel
    return (
      raw
        .replace(/[^\p{L}\p{N}]+/gu, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60) || "deliverable"
    )
  }

  const handleExportCsv = () => {
    if (!csvPreview) {
      toast.error(t("deepExplorer.export_csv_no_table"))
      return
    }
    downloadCsv(csvPreview.csv, `${deriveBaseFilename()}.csv`)
    toast.success(t("deepExplorer.export_csv_success", { count: csvPreview.rowCount }))
  }

  const handleExportSvg = () => {
    const ok = downloadSvg(contentRef.current, `${deriveBaseFilename()}.svg`)
    if (ok) toast.success(t("deepExplorer.export_svg_success"))
    else toast.error(t("deepExplorer.export_svg_error"))
  }

  const handleExportPdf = () => {
    const ok = printDeliverableAsPdf(contentRef.current, deriveBaseFilename())
    if (!ok) toast.error(t("deepExplorer.export_pdf_error"))
  }

  return (
    <section
      aria-label={t("deepExplorer.deliverable_aria", { format: formatLabel })}
      className="flex h-full flex-col overflow-hidden"
    >
      {/* Format badge header */}
      <header
        className={cn(
          "border-border flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2",
          isStreaming && "animate-pulse motion-reduce:animate-none"
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              "inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium",
              info.accentClass
            )}
          >
            {info.icon}
            <span>{formatLabel}</span>
          </span>
          {stepCount > 0 && (
            <span className="text-muted-foreground shrink-0 text-[11px]">
              {t("deepExplorer.step_count", { count: stepCount })}
            </span>
          )}
          {citationCount > 0 && (
            <button
              className="text-muted-foreground hover:text-foreground focus-visible:ring-ring shrink-0 rounded text-[11px] underline decoration-dotted underline-offset-2 focus-visible:ring-2 focus-visible:outline-none"
              type="button"
              title={t("deepExplorer.jump_to_sources", {
                defaultValue: "Show sources",
              })}
              onClick={handleSourcesBadgeClick}
            >
              {t("deepExplorer.citation_count", { count: citationCount })}
            </button>
          )}
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-0.5">
          {headerActions}
          {canExportCsv && (
            <button
              className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none"
              title={t("deepExplorer.export_csv")}
              type="button"
              onClick={handleExportCsv}
            >
              <FileSpreadsheet className="size-3.5" />
            </button>
          )}
          {hasMermaid && (
            <button
              className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none"
              title={t("deepExplorer.export_svg")}
              type="button"
              onClick={handleExportSvg}
            >
              <ImageIcon className="size-3.5" />
            </button>
          )}
          <button
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none"
            title={t("deepExplorer.export_pdf")}
            type="button"
            onClick={handleExportPdf}
          >
            <Printer className="size-3.5" />
          </button>
        </div>
      </header>

      {/* Body — TOC sidebar (when applicable) + content */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {toc.length >= 3 && (
          <aside
            aria-label={t("deepExplorer.table_of_contents")}
            className="border-border bg-muted/30 hidden w-44 shrink-0 overflow-y-auto border-r px-3 py-3 text-xs md:block"
          >
            <p className="text-muted-foreground mb-2 text-[10px] font-semibold tracking-wide uppercase">
              {t("deepExplorer.table_of_contents")}
            </p>
            <ol className="space-y-1">
              {toc.map((entry) => (
                <li
                  key={`${entry.level}-${entry.anchor}`}
                  className={cn(
                    entry.level === 3 && "ml-3",
                    "text-muted-foreground hover:text-foreground leading-tight transition-colors"
                  )}
                >
                  <button
                    className="focus-visible:ring-ring block w-full truncate rounded text-left focus-visible:ring-2 focus-visible:outline-none"
                    title={entry.text}
                    type="button"
                    onClick={() => scrollToAnchor(entry.anchor)}
                  >
                    {entry.text}
                  </button>
                </li>
              ))}
            </ol>
          </aside>
        )}

        <div className="min-h-0 flex-1 overflow-auto">
          <div ref={contentRef} className="p-4" role="presentation" onClick={handleContentClick}>
            {useCanvasBlocks ? (
              <div className="prose-sm max-w-none">
                {canvasBlocks.map((block, i) => (
                  <CanvasBlockRenderer key={i} block={block} onCitationClick={onCitationClick} />
                ))}
              </div>
            ) : (
              <MarkdownRenderer
                className="prose-sm max-w-none"
                components={citationComponents}
                content={answerHtml}
                isStreaming={isStreaming}
              />
            )}
          </div>
        </div>
      </div>
      {/* Inline hover-peek mirroring chat citations: scans the content ref
          for `.supContainer` badges and renders a portaled preview card on
          hover. The deliverable doesn't carry per-chunk text the way chat
          responses do, so URL chunk lookup returns null. */}
      {citationSection && onCitationViewDocument && !useCanvasBlocks && (
        <CitationInlinePeek
          answerTextRef={contentRef}
          citations={citations}
          citationSection={citationSection}
          disabled={isStreaming}
          findChunkForUrlCitation={() => null}
          onViewDocument={onCitationViewDocument}
        />
      )}
    </section>
  )
}
